import { isNumber, throttle } from 'lodash';
import { MathsKitManager } from '../manager';
import {  MarkCircle, MathsKit, MathsKitOptions } from '../mathsKit';
import { MathsKitElement, MathsKitType } from '../types';
import Vec2d, { CircleSegment, decomposeMatrix, LineSegment, Matrix2D, Point2d } from '../utils';


export type ProtractorElement = HTMLDivElement;

export const angle_radian = -1;

export class ProtractorTool extends MathsKit {

  showLineLength(_number: number): void {
    // throw new Error('Method not implemented.');
  }
  hideLineLength(): void {
    // throw new Error('Method not implemented.');
  }
  protected markLines: MarkCircle[] = [];
  private deleteBtn: HTMLDivElement = document.createElement('div');
  private rotateBtn: HTMLDivElement = document.createElement('div');
  private angleBtnLeft: HTMLDivElement = document.createElement('div');
  private angleBtnRight: HTMLDivElement = document.createElement('div');
  private stretchBtn!: HTMLDivElement;
  private elementContent!: HTMLDivElement;
  private circleMark!: HTMLDivElement;
  private tragetAngleBtn?: HTMLDivElement;
  private originZeroPoint?: Point2d;
  private originalEventPoint?: Point2d;
  private originAnglePoint?: Point2d;

  bindElement(element: MathsKitElement, type: MathsKitType) {
    this.element.classList.add('maths-kit', `maths-kit-${type}`, `maths-kit-${this.manager.theme}`);
    this.manager.container.appendChild(element);
  }

  private bindElementContent(element: MathsKitElement){
    this.elementContent = document.createElement('div');
    this.elementContent.classList.add('maths-kit-content');
    this.elementContent.setAttribute('data-id', this.id);
    element.appendChild(this.elementContent);
  }
  
  constructor(options: MathsKitOptions, manager: MathsKitManager) {
    super(options, manager);
    this.bindElementContent(this.element);
  }

  init(): void {
    this.circleMark = this.createMark(this.elementContent, 'circle');
    this.createAngleBtnLeft();
    this.createAngleBtnRight();
    this.createDeleteBtn();
    this.createRotateBtn();
    this.createStretchBtn();
    this.bindEvent();
    this.render();
  }

  onDragStart(): void {
    this.toTop();
  }

  onDragDoing(): void {}

  onDragEnd(matrix: Matrix2D): void {
    this.updateStateMatrix(matrix);
  }

  onRotateStart(_originAngle: number): void {
    this.toTop();
  }

  onRotateDoing(_angle: number): void {
  }

  onRotateEnd(matrix: Matrix2D): void {
    this.updateStateMatrix(matrix);
  }


  getStretchDirection(): 'x' | 'y' {
    return 'y';
  }

  onStretchStart(): void {
    this.toTop();
    this.thottled.cancel();
  }

  private thottled = throttle((_width: number): void => {
    this.element.style.width = `${_width}px`;
    this.elementContent.style.width = `${_width}px`;
    this.elementContent.style.height = `${this.computeHeight(_width)}px`;
    this.renderMark();
  },50);

  onStretchDoing = (_width: number): void => {
    this.thottled(_width);    
  };

  onStretchEnd(width: number): void {
    this.thottled.cancel();
    // 更新三角形的宽度
    this.manager.update(this.id, { width: width });
  }

  /**
   * 获取旋转中心点, 坐标必须相对于容器左上角, 旋转中心点是左上角
   * @returns 
   */
  getRotateCenterPoint = () => {
    const { x, y } = this.state;
    const container = this.manager.container;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const pxX = x * containerWidth;
    const pxY = y * containerHeight;
    const domMatrix = this.getDomMatrix();
    const { translateX, translateY } = decomposeMatrix(domMatrix);
    const circleCenterPoint = new Vec2d(pxX + translateX, pxY + translateY);
    // 返回 Point2d 对象
    return new Point2d(circleCenterPoint.x, circleCenterPoint.y);
  };

  getMarkLines(): MarkCircle[] {
    const { width } = this.state;
    const { scaleY, rotation } = decomposeMatrix(this.getDomMatrix());
    const height = this.computeHeight(width);
    const baseHeight = height * scaleY;
    // 计算圆心
    const circleCenterPoint = this.getRotateCenterPoint();
    const circleSegment = new CircleSegment(circleCenterPoint, baseHeight, 0, 180 * Math.PI / 180, angle_radian);
    circleSegment.rotate(rotation);

    const markLines: MarkCircle[] = [
      {
        axis: 'circle',
        circle: circleSegment,
      },
    ];
    return markLines;
  }

  private bindEvent() {
    this.deleteBtn.addEventListener('mouseenter', this.onBtnMouseEnterHandler);
    this.deleteBtn.addEventListener('mouseleave', this.onBtnMouseLeaveHandler);
    this.deleteBtn.addEventListener('click', this.onDeleteBtnClickHandler);

    this.stretchBtn.addEventListener('mouseenter', this.onBtnMouseEnterHandler);
    this.stretchBtn.addEventListener('mouseleave', this.onBtnMouseLeaveHandler);
    
    this.rotateBtn.addEventListener('mouseenter', this.onBtnMouseEnterHandler);
    this.rotateBtn.addEventListener('mouseleave', this.onBtnMouseLeaveHandler);
    
    if (this.angleBtnLeft && this.angleBtnLeft.firstElementChild) {
      (this.angleBtnLeft.firstElementChild as HTMLDivElement).addEventListener('pointerdown', this.rotateAngleBtnHandle);
    }

    if (this.angleBtnRight && this.angleBtnRight.firstElementChild) {
      (this.angleBtnRight.firstElementChild as HTMLDivElement).addEventListener('pointerdown', this.rotateAngleBtnHandle);
    }
  }

  private unbindEvent() {
    if (this.deleteBtn) {
      this.deleteBtn.removeEventListener('pointerenter', this.onBtnMouseEnterHandler);
      this.deleteBtn.removeEventListener('pointerleave', this.onBtnMouseLeaveHandler);
      this.deleteBtn.removeEventListener('click', this.onDeleteBtnClickHandler);
    }

    if (this.stretchBtn) {
      this.stretchBtn.removeEventListener('pointerenter', this.onBtnMouseEnterHandler);
      this.stretchBtn.removeEventListener('pointerleave', this.onBtnMouseLeaveHandler);
    }
    
    if (this.rotateBtn) {
      this.rotateBtn.removeEventListener('pointerenter', this.onBtnMouseEnterHandler);
      this.rotateBtn.removeEventListener('pointerleave', this.onBtnMouseLeaveHandler);
    }
    if (this.angleBtnLeft && this.angleBtnLeft.firstElementChild) {
      (this.angleBtnLeft.firstElementChild as HTMLDivElement).removeEventListener('pointerdown', this.rotateAngleBtnHandle);
    }
    if (this.angleBtnRight && this.angleBtnRight.firstElementChild) {
      (this.angleBtnRight.firstElementChild as HTMLDivElement).removeEventListener('pointerdown', this.rotateAngleBtnHandle);
    }
    this.thottled.cancel();
  }

  private computAngle(e: PointerEvent) {
    if (!this.originalEventPoint || !this.tragetAngleBtn || !this.originZeroPoint || !this.originAnglePoint) {
      return;
    }
    const currentPoint = new Point2d(e.pageX, e.pageY);
    const offsetPoint = Point2d.Sub(this.originalEventPoint, currentPoint);
    const currentAnglePoint = Point2d.Sub(this.originAnglePoint, offsetPoint);
    const angle = Math.round(this.getRotateCenterPoint().getAngleByPoints(currentAnglePoint, this.originZeroPoint));
    return angle;
  }

  private onAngleBtnPointerMoveHandler = (e: PointerEvent) => {
    e.stopPropagation();
    if (!this.originZeroPoint || !this.originalEventPoint || !this.tragetAngleBtn || !this.originAnglePoint) {
      return;
    }
    const { custom } = this.state;
    const { startAngle, endAngle } = custom || {};
    if (!isNumber(startAngle) || !isNumber(endAngle)) {
      return;
    }
    const angle = this.computAngle(e);
    if (!isNumber(angle)) {
      return;
    }
    const type = this.tragetAngleBtn.getAttribute('data-type') as 'left' | 'right';
    if(type === 'left') {
      this.angleBtnLeft.style.transform = `rotate(${angle * angle_radian}deg)`;
      (this.angleBtnLeft.firstElementChild as HTMLDivElement).textContent = `${Math.abs(angle - startAngle)}°`;
      (this.angleBtnRight.firstElementChild as HTMLDivElement).textContent = `${Math.abs(angle - startAngle)}°`;
    } else {
      this.angleBtnRight.style.transform = `rotate(${angle * angle_radian}deg)`;
      (this.angleBtnLeft.firstElementChild as HTMLDivElement).textContent = `${Math.abs(angle - endAngle)}°`;
      (this.angleBtnRight.firstElementChild as HTMLDivElement).textContent = `${Math.abs(angle - endAngle)}°`;
    }
    this.tragetAngleBtn.style.transform = `translateY(-50%) rotate(${angle}deg)`;
  };
  private stopAngleBtnPointerMoveHandler = (e: PointerEvent) => {
    e.stopPropagation();
    if (!this.originalEventPoint || !this.tragetAngleBtn) {
      return;
    }
    const { custom } = this.state;
    const { startAngle, endAngle } = custom || {};
    if (!isNumber(startAngle) || !isNumber(endAngle)) {
      return;
    }
    const angle = this.computAngle(e);
    if (!isNumber(angle)) {
      return;
    }
    const type = this.tragetAngleBtn.getAttribute('data-type') as 'left' | 'right';
    document.removeEventListener('pointermove', this.onAngleBtnPointerMoveHandler, true);
    document.removeEventListener('pointerup', this.stopAngleBtnPointerMoveHandler, true);
    document.removeEventListener('pointercancel', this.stopAngleBtnPointerMoveHandler, true);
    this.tragetAngleBtn.style.cursor = 'pointer';
    this.circleMark.style.pointerEvents = this._isActive ? 'auto' : 'none';
    this.tragetAngleBtn = undefined;
    this.originalEventPoint = undefined;
    this.originZeroPoint = undefined;
    // let currentAngle = 0;
    if(type === 'left') {
      // currentAngle = Math.max(0, Math.min(180, endAngle + angle));
      this.manager.update(this.id, { custom: { startAngle, endAngle: angle } });
    } else {
      // currentAngle = Math.max(0, Math.min(180, startAngle + angle));
      this.manager.update(this.id, { custom: { startAngle: angle, endAngle } });
    }
  };
  private getCurrentAngle(): number {
    // 从状态矩阵中提取旋转角度，而不是显示矩阵
    const stateMatrix = new Matrix2D(...this.state.matrix);
    const [a, b] = stateMatrix.toArray();
    let angle = Math.round(Math.atan2(b, a) * (180 / Math.PI) * 100) / 100;
    // 将角度转换为与 getAngleByPoints 一致的方向和范围
    angle = -angle; // 取负值以匹配 getAngleByPoints 的方向
    
    // 确保角度在 0-360 范围内
    if (angle < 0) {
      angle += 360;
    }
    return angle;
  }
  private rotateAngleBtnHandle = (e: PointerEvent) => {
    e.stopPropagation();
    const target = e.currentTarget as HTMLDivElement;
    this.tragetAngleBtn = target;
    const { x, y, width, custom } = this.state;
    if (!isNumber(custom?.startAngle) || !isNumber(custom?.endAngle)) {
      return;
    }
    const container = this.manager.container;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const pxX = x * containerWidth;
    const pxY = y * containerHeight;
    const { translateX, translateY, scaleX } = decomposeMatrix(this.getDomMatrix());
    const baseWidth = width * scaleX;
    this.originZeroPoint = new Point2d(pxX + baseWidth / 2, pxY);
    this.originZeroPoint.add(new Vec2d(translateX, translateY));

    const currentAngle = this.getCurrentAngle();
    const angleInRadians = (currentAngle * Math.PI) / 180;
    const rotateCenterPoint = this.getRotateCenterPoint();
    this.originZeroPoint.rotWith(rotateCenterPoint, -angleInRadians);
    this.originalEventPoint = new Point2d(e.pageX, e.pageY);
    const type = this.tragetAngleBtn.getAttribute('data-type') as 'left' | 'right';
    const angle = type === 'left' ? custom?.endAngle : custom?.startAngle;
    this.originAnglePoint = Point2d.RotWith(this.originZeroPoint, rotateCenterPoint, (angle * Math.PI) / 180 * angle_radian);
    document.addEventListener('pointermove', this.onAngleBtnPointerMoveHandler, true);
    document.addEventListener('pointerup', this.stopAngleBtnPointerMoveHandler, true);
    document.addEventListener('pointercancel', this.stopAngleBtnPointerMoveHandler, true);
    this.tragetAngleBtn.style.cursor = 'none';
    this.circleMark.style.pointerEvents = 'none';
  };

  destroy(): void {
    this.circleMark.remove();
    this.elementContent.remove();
    super.destroy();
    this.unbindEvent();
  }

  private onDeleteBtnClickHandler = (e: MouseEvent) => {
    e.stopPropagation();
    this.manager.remove(this.id);
  };

  private onBtnMouseEnterHandler = (e: MouseEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.style.background = 'var(--maths-kit-math-kit-btn-hover-bg, #f0f4ff)';
    if (e.target === this.stretchBtn) {
      target.style.transform = 'translateX(-50%) scale(1.1) rotate(-90deg)';
    } else {
      target.style.transform = 'scale(1.1)';
    }
  };

  private onBtnMouseLeaveHandler = (e: MouseEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.style.background = 'var(--maths-kit-math-kit-btn-bg, #fff)';
    if (e.target === this.stretchBtn) {
      target.style.transform = 'translateX(-50%) scale(1) rotate(-90deg)';
    } else {
      target.style.transform = 'scale(1)';
    }
  };

  createElement(): HTMLDivElement {
    const element = document.createElement('div');
    // 确保容器有正确的定位上下文
    element.style.position = 'relative';
    element.style.transformOrigin = 'left bottom';
    element.style.touchAction = 'none';

    return element;
  }

  private createMark(element: HTMLDivElement, position: 'circle') {
    const mark = document.createElement('div');
    mark.classList.add('maths-kit-protractor-mark', position);
    // 应用内联样式
    Object.assign(mark.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      border: 'none',
      backgroundColor: 'var(--maths-kit-ruler-bg, rgba(255, 255, 255, 0.8))',
      pointerEvents: this._isActive ? 'auto' : 'none',
      cursor: 'pointer',
      overflow: 'hidden',
    });
    mark.setAttribute('data-type', 'Drag');
    mark.setAttribute('data-id', this.id);
    element.appendChild(mark);
    return mark;
  }

  private computeHeight(width: number) {
    return width / 2;
  }

  render(): void {
    const state = this.state;
    const domMatrix = this.getDomMatrix();
    const height = this.computeHeight(state.width);
    const width = state.width;

    const style = {
      position: 'absolute',
      left: `${state.x * 100}%`,
      top: `${state.y * 100}%`,
      height: '0',
      width: `${width}px`,
      minWidth: '300px',
      transform: domMatrix.toCSSTransform(),
      zIndex: state.zIndex,
      boxSizing: 'content-box',
    };
    Object.assign(this.element.style, style);

    const elementContentStyle = {
      position: 'absolute',
      bottom: '0',
      left: '0',
      transform: 'translateX(-50%)',
      transformOrigin: 'left bottom',
      width: `${width}px`,
      height: `${height}px`,
      minWidth: '300px',
      minHeight: `${this.computeHeight(300)}px`,
      boxSizing: 'border-box',
      border: 'none',
      pointerEvents: 'none',
    };
    Object.assign(this.elementContent.style, elementContentStyle);

    this.angleBtnLeft.style.width = `${width / 2 + 50}px`;
    this.angleBtnRight.style.width = `${width / 2 + 50}px`;

    if (state.custom && isNumber(state.custom.startAngle) && isNumber(state.custom.endAngle) && this.angleBtnLeft.firstElementChild && this.angleBtnRight.firstElementChild) {
      const { startAngle, endAngle } = state.custom;
      this.angleBtnLeft.style.transform = `rotate(${endAngle * angle_radian}deg)`;
      this.angleBtnRight.style.transform = `rotate(${startAngle * angle_radian}deg)`;
      (this.angleBtnLeft.firstElementChild as HTMLDivElement).style.transform = `translateY(-50%) rotate(${endAngle}deg)`;
      (this.angleBtnRight.firstElementChild as HTMLDivElement).style.transform = `translateY(-50%) rotate(${startAngle}deg)`;
      const startAngleText = Math.abs(startAngle - endAngle);
      (this.angleBtnLeft.firstElementChild as HTMLDivElement).textContent = `${startAngleText}°`;
      (this.angleBtnRight.firstElementChild as HTMLDivElement).textContent = `${startAngleText}°`;
    }

    // 使用setTimeout确保DOM完全渲染后再获取尺寸和重新绑定事件
    setTimeout(() => {
      this.renderMark();
    }, 0);

  }
  private renderMark() {
    this.renderMarkAtPosition(this.circleMark);
  }

  private renderMarkAtPosition(mark: HTMLDivElement) {
    // 清空现有内容
    mark.innerHTML = '';

    const svgWidth = this.elementContent.offsetWidth;
    const svgHeight = this.elementContent.offsetHeight;
    mark.style.width = `${svgWidth}px`; // 减少高度，因为刻度线变短了
    mark.style.height = `${svgHeight}px`;
    mark.style.borderRadius = `${svgHeight}px ${svgHeight}px 0 0`;
    
    const circleCenter = new Point2d(svgWidth / 2, svgHeight);
    // 创建SVG容器
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', `${svgWidth}`);
    svg.setAttribute('height', `${svgHeight}`); // 减少SVG高度
    svg.style.position = 'absolute';
    svg.style.top = '0';
    svg.style.left = '0';
    svg.style.pointerEvents = 'none';

    const outDonutRadius = 18;
    const middleDonutRadius = 26;
    const innerDonutRadius = 16;
    const innerCircleRadius = 30;
    // 添加半透明背景矩形
    const backgroundCircle1 = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    backgroundCircle1.setAttribute('cx', `${circleCenter.x}`);
    backgroundCircle1.setAttribute('cy', `${circleCenter.y}`);
    backgroundCircle1.setAttribute('r', `${svgHeight - outDonutRadius / 2}`);
    backgroundCircle1.setAttribute('stroke', 'var(--maths-kit-ruler-mark-bg, rgba(240, 240, 240, 0.8))');
    backgroundCircle1.setAttribute('stroke-width', `${outDonutRadius}`);
    backgroundCircle1.setAttribute('fill', 'transparent');

    const backgroundCircle2 = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    backgroundCircle2.setAttribute('cx', `${circleCenter.x}`);
    backgroundCircle2.setAttribute('cy', `${circleCenter.y}`);
    backgroundCircle2.setAttribute('r', `${svgHeight - middleDonutRadius - outDonutRadius - innerDonutRadius * 1.5}`);
    backgroundCircle2.setAttribute('stroke', 'var(--maths-kit-ruler-mark-bg, rgba(240, 240, 240, 0.8))');
    backgroundCircle2.setAttribute('stroke-width', `${innerDonutRadius}`);
    backgroundCircle2.setAttribute('fill', 'transparent');

    const centerCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    centerCircle.setAttribute('cx', `${circleCenter.x}`);
    centerCircle.setAttribute('cy', `${circleCenter.y}`);
    centerCircle.setAttribute('r', '1');
    centerCircle.setAttribute('fill', 'var(--maths-kit-ruler-major-mark, #000)');

    const centerCircle1 = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    centerCircle1.setAttribute('cx', `${circleCenter.x}`);
    centerCircle1.setAttribute('cy', `${circleCenter.y}`);
    centerCircle1.setAttribute('r', '2');
    centerCircle1.setAttribute('stroke-width', '1');
    centerCircle1.setAttribute('stroke', 'var(--maths-kit-ruler-medium-mark, #666)');
    centerCircle1.setAttribute('fill', 'transparent');

    svg.append(backgroundCircle1, backgroundCircle2, centerCircle, centerCircle1);
    // 刻度线数量
    const totalMarks = 180;
    // 刻度线宽度：1px
    const MARK_WIDTH_PX = 1;

    const angleLines = Point2d.GetSemicircleStroke(circleCenter, new Point2d(circleCenter.x + svgHeight, circleCenter.y), angle_radian, totalMarks);

    // 创建刻度线和数值
    for (let i = 0; i <= totalMarks; i++) {
      
      // 确定刻度线高度
      let markHeight = 5; // 默认小刻度高度
      // 是否为大刻度
      let isMajorMark = false;
      // 是否为中等刻度
      let isMediumMark = false;
      
      // 每10角度为大刻度
      if (i % 10 === 0) {
        markHeight = 18;
        isMajorMark = true;
      } else if (i % 5 === 0) {
        // 每5mm为中等刻度
        markHeight = 10;
        isMediumMark = true;
      }
      // 创建刻度线
      const x1 = angleLines[i].x;
      const y1 = angleLines[i].y;
      const lineSegment = new LineSegment(new Vec2d(x1, y1), circleCenter);
      // 计算在lineSegment上距离lineSegment起点markHeight的点
      const t = lineSegment.length > 0 ? markHeight / lineSegment.length : 0;
      const point = lineSegment.getPointAt(t);
      const x2 = point.x;
      const y2 = point.y;
      const line1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line1.setAttribute('x1', `${x1}`);
      line1.setAttribute('y1', `${y1}`);
      line1.setAttribute('x2', `${x2}`);
      line1.setAttribute('y2', `${y2}`);
      line1.setAttribute('stroke', isMajorMark ? 'var(--maths-kit-ruler-major-mark, #000)' : isMediumMark ? 'var(--maths-kit-ruler-medium-mark, #666)' : 'var(--maths-kit-ruler-minor-mark, #999)');
      line1.setAttribute('stroke-width', `${MARK_WIDTH_PX}`);
      line1.setAttribute('stroke-opacity', isMajorMark ? '1' : isMediumMark ? '0.8' : '0.6');
      svg.appendChild(line1);
      // 为大刻度添加数值标签
      if (isMajorMark) {
        const t1 = lineSegment.length > 0 ? (middleDonutRadius + outDonutRadius + innerDonutRadius) / lineSegment.length : 0;
        const t4 = lineSegment.length > 0 ? (lineSegment.length - innerCircleRadius) / lineSegment.length : 0;
        const point1 = lineSegment.getPointAt(t1);
        const point4 = lineSegment.getPointAt(t4);
        const line2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line2.setAttribute('x1', `${point1.x}`);
        line2.setAttribute('y1', `${point1.y}`);
        line2.setAttribute('x2', `${point4.x}`);
        line2.setAttribute('y2', `${point4.y}`);
        line2.setAttribute('stroke', 'var(--maths-kit-ruler-major-mark, #000)');
        line2.setAttribute('stroke-width', `${MARK_WIDTH_PX}`);

        const t2 = lineSegment.length > 0 ? (outDonutRadius + middleDonutRadius / 2) / lineSegment.length : 0;
        const ponitText = lineSegment.getPointAt(t2);
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.textContent = `${i}`;
        text.setAttribute('x', `${ponitText.x}`);
        text.setAttribute('y', `${ponitText.y}`);
        text.setAttribute('text-anchor', 'middle');
        text.setAttribute('font-size', '11px');
        text.setAttribute('fill', 'var(--maths-kit-ruler-text, #000)');
        text.setAttribute('font-family', 'Arial, sans-serif');

        const t3 = lineSegment.length > 0 ? (outDonutRadius + middleDonutRadius + innerDonutRadius / 2) / lineSegment.length : 0;
        const ponitText1 = lineSegment.getPointAt(t3);
        const text1 = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text1.textContent = `${totalMarks - i}`;
        text1.setAttribute('x', `${ponitText1.x}`);
        text1.setAttribute('y', `${ponitText1.y}`);
        text1.setAttribute('text-anchor', 'middle');
        text1.setAttribute('font-size', '11px');
        text1.setAttribute('fill', 'var(--maths-kit-ruler-text, #000)');
        text1.setAttribute('font-family', 'Arial, sans-serif');
        svg.append(line2, text, text1);
      }
    }
    
    mark.appendChild(svg);
  }

  setActive(isActive: boolean) {
    this._isActive = isActive;
    if (isActive) {
      this.circleMark.style.pointerEvents = 'auto';
      if (this.rotateBtn) {
        this.rotateBtn.style.pointerEvents = 'auto';
      }
      if (this.stretchBtn) {
        this.stretchBtn.style.pointerEvents = 'auto';
      }
      if (this.deleteBtn) {
        this.deleteBtn.style.pointerEvents = 'auto';
      }
      if (this.angleBtnLeft && this.angleBtnLeft.firstElementChild) {
        (this.angleBtnLeft.firstElementChild as HTMLDivElement).style.pointerEvents = 'auto';
      }
      if (this.angleBtnRight && this.angleBtnRight.firstElementChild) {
        (this.angleBtnRight.firstElementChild as HTMLDivElement).style.pointerEvents = 'auto';
      }
    } else {
      this.circleMark.style.pointerEvents = 'none';
      if (this.rotateBtn) {
        this.rotateBtn.style.pointerEvents = 'none';
      }
      if (this.stretchBtn) {
        this.stretchBtn.style.pointerEvents = 'none';
      }
      if (this.deleteBtn) {
        this.deleteBtn.style.pointerEvents = 'none';
      }
      if (this.angleBtnLeft && this.angleBtnLeft.firstElementChild) {
        (this.angleBtnLeft.firstElementChild as HTMLDivElement).style.pointerEvents = 'none';
      }
      if (this.angleBtnRight && this.angleBtnRight.firstElementChild) {
        (this.angleBtnRight.firstElementChild as HTMLDivElement).style.pointerEvents = 'none';
      }
    }
  }

  private createAngleBtnLeft() {
    this.angleBtnLeft.classList.add('maths-kit-math-kit-angle-btn', 'left');
    // 应用内联样式
    Object.assign(this.angleBtnLeft.style, {
      position: 'absolute',
      bottom: '0',
      left: '0',
      height: '1px',
      background: 'var(--maths-kit-math-kit-angle-btn-bg, #00A870)',
      border: 'none',
      fontSize: '12px',
      zIndex: '2',
      transformOrigin: 'left center',
      pointerEvents: 'none',
    });
    const child = document.createElement('div');
    Object.assign(child.style, {
      position: 'absolute',
      right: '0',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '30px',
      height: '30px',
      border: '1px solid var(--maths-kit-math-kit-angle-btn-bg, #00A870)',
      background: 'var(--maths-kit-math-kit-btn-bg, #fff)',
      borderRadius: '50%',
      cursor: 'pointer',
      lineHeight: '30px',
      textAlign: 'center',
      color: 'var(--maths-kit-math-kit-btn-text, #000)',
      zIndex: '1000',
      pointerEvents: this._isActive ? 'auto' : 'none',
    });
    child.setAttribute('data-type', 'left');
    this.angleBtnLeft.appendChild(child);
    this.element.appendChild(this.angleBtnLeft);
  }

  private createAngleBtnRight() {
    this.angleBtnRight.classList.add('maths-kit-math-kit-angle-btn', 'right');
    // 应用内联样式
    Object.assign(this.angleBtnRight.style, {
      position: 'absolute',
      bottom: '0',
      left: '0',
      height: '1px',
      background: 'var(--maths-kit-math-kit-angle-btn-bg, #00A870)',
      border: 'none',
      fontSize: '12px',
      zIndex: '2',
      transformOrigin: 'left center',
      pointerEvents: 'none',
    });
    const child = document.createElement('div');
    Object.assign(child.style, {
      position: 'absolute',
      right: '0',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '30px',
      height: '30px',
      border: '1px solid var(--maths-kit-math-kit-angle-btn-bg, #00A870)',
      background: 'var(--maths-kit-math-kit-btn-bg, #fff)',
      borderRadius: '50%',
      cursor: 'pointer',
      lineHeight: '30px',
      textAlign: 'center',
      color: 'var(--maths-kit-math-kit-btn-text, #000)',
      zIndex: '1000',
      pointerEvents: this._isActive ? 'auto' : 'none',
    });
    child.setAttribute('data-type', 'right');
    this.angleBtnRight.appendChild(child);
    this.element.appendChild(this.angleBtnRight);
  }

  private createDeleteBtn() {
    this.deleteBtn.classList.add('maths-kit-math-kit-angle-btn');
    
    // 应用内联样式
    Object.assign(this.deleteBtn.style, {
      position: 'absolute',
      bottom: '15px',
      left: '20%',
      width: '20px',
      height: '20px',
      transform: 'scale(1)',
      background: 'var(--maths-kit-math-kit-btn-bg, #fff)',
      border: '2px solid var(--maths-kit-math-kit-btn-border, #fff)',
      borderRadius: '50%',
      cursor: 'pointer',
      lineHeight: '19px',
      textAlign: 'center',
      color: 'var(--maths-kit-math-kit-btn-text, #000)',
      fontSize: '12px',
      fontWeight: 'bold',
      boxShadow: '0 4px 12px 0 var(--maths-kit-math-kit-btn-shadow, rgba(0, 0, 0, 0.08))',
      zIndex: '1000',
      pointerEvents: this._isActive ? 'auto' : 'none',
    });
    
    // 添加删除图标
    this.deleteBtn.textContent = '×';
    this.elementContent.appendChild(this.deleteBtn);
  }

  private createRotateBtn() {
    this.rotateBtn.classList.add('maths-kit-math-kit-btn-btn');
    
    // 应用内联样式
    Object.assign(this.rotateBtn.style, {
      position: 'absolute',
      bottom: '15px',
      right: '20%',
      width: '20px',
      height: '20px',
      transform: 'scale(1)',
      background: 'var(--maths-kit-math-kit-btn-bg, #fff)',
      border: '2px solid var(--maths-kit-math-kit-btn-border, #fff)',
      borderRadius: '50%',
      cursor: 'pointer',
      lineHeight: '19px',
      textAlign: 'center',
      color: 'var(--maths-kit-math-kit-btn-text, #000)',
      fontSize: '12px',
      fontWeight: 'bold',
      boxShadow: '0 4px 12px 0 var(--maths-kit-math-kit-btn-shadow, rgba(0, 0, 0, 0.08))',
      zIndex: '1000',
      pointerEvents: this._isActive ? 'auto' : 'none',
    });
    
    // 添加旋转图标
    this.rotateBtn.textContent = '↻';
    this.rotateBtn.setAttribute('data-type', 'Rotate');
    this.elementContent.appendChild(this.rotateBtn);
  }

  private createStretchBtn() {
    this.stretchBtn = document.createElement('div');
    this.stretchBtn.classList.add('maths-kit-math-kit-btn-btn');
    
    // 应用内联样式
    Object.assign(this.stretchBtn.style, {
      position: 'absolute',
      top: '65px',
      left: '50%',
      width: '20px',
      height: '20px',
      transform: 'translateX(-50%) scale(1) rotate(-90deg)',
      background: 'var(--maths-kit-math-kit-btn-bg, #fff)',
      border: '2px solid var(--maths-kit-math-kit-btn-border, #fff)',
      borderRadius: '50%',
      cursor: 'pointer',
      lineHeight: '19px',
      textAlign: 'center',
      color: 'var(--maths-kit-math-kit-btn-text, #000)',
      fontSize: '12px',
      fontWeight: 'bold',
      boxShadow: '0 4px 12px 0 var(--maths-kit-math-kit-btn-shadow, rgba(0, 0, 0, 0.08))',
      zIndex: '1000',
      pointerEvents: this._isActive ? 'auto' : 'none',
    });
    
    // 添加拉伸图标
    this.stretchBtn.textContent = '⇥';
    this.stretchBtn.setAttribute('data-type', 'Stretch');
    this.elementContent.appendChild(this.stretchBtn);
  }

}